import { useState } from "react";
import { api } from "../../lib/api.js";
import { Logo } from "../../components/ui.js";

interface Candidate {
  id: string;
  category: string;
  label: string;
  value: string | null;
  sourceText: string | null;
}

export interface FinalizeResult {
  factsWritten: number;
  medicationsCreated: number;
  extractionRan: boolean;
  candidates: Candidate[];
  summary: {
    highlights: { label: string; value: string }[];
    recovery: { active: boolean; note: string | null };
    narrative: string;
    narrativeSource: string;
  };
}

export function ReviewStep({
  result,
  onBack,
  onComplete
}: {
  result: FinalizeResult;
  onBack: () => void;
  onComplete: () => void;
}) {
  const [candidates, setCandidates] = useState<Candidate[]>(result.candidates);
  const [completing, setCompleting] = useState(false);

  async function resolve(id: string, action: "accept" | "reject") {
    setCandidates((prev) => prev.filter((c) => c.id !== id));
    await api.post(`/api/profile/candidates/${id}/${action}`).catch(() => undefined);
  }

  return (
    <div className="onboarding">
      <div className="onboarding-body" style={{ paddingTop: "var(--space-8)" }}>
        <div className="row" style={{ gap: "var(--space-2)" }}>
          <Logo size={32} />
          <span style={{ fontWeight: 700 }}>Meet ClovaForm</span>
        </div>

        <div className="card card-pad-lg mt-6">
          <p style={{ fontSize: "var(--text-lg)", lineHeight: 1.6 }}>{result.summary.narrative}</p>
          {result.summary.narrativeSource !== "assembled" && (
            <p className="dim text-xs mt-4">Summarized by {result.summary.narrativeSource}.</p>
          )}
        </div>

        {result.summary.recovery.active && (
          <div className="alert alert-info mt-4">
            <strong>Recovery Mode is on.</strong> Healing comes first — ClovaForm
            won’t prescribe strenuous exercise until you record medical clearance.
            {result.summary.recovery.note ? ` Your noted limits: ${result.summary.recovery.note}` : ""}
          </div>
        )}

        {result.summary.highlights.length > 0 && (
          <div className="stat-grid mt-4">
            {result.summary.highlights.map((h) => (
              <div className="stat" key={h.label}>
                <div className="stat-label">{h.label}</div>
                <div style={{ fontWeight: 600, marginTop: 4 }}>{h.value}</div>
              </div>
            ))}
          </div>
        )}

        {candidates.length > 0 && (
          <div className="mt-8">
            <h2>A few things I picked up</h2>
            <p className="muted text-sm mt-2">
              I extracted these from what you wrote. Save the ones that are right,
              and skip the ones that aren’t — nothing is saved as a fact until you
              say so.
            </p>
            <div className="stack mt-4">
              {candidates.map((c) => (
                <div className="card" key={c.id}>
                  <div className="row-between wrap" style={{ gap: "var(--space-2)" }}>
                    <div className="grow">
                      <span className="badge badge-muted">{c.category}</span>
                      <div style={{ fontWeight: 600, marginTop: 6 }}>{c.label}</div>
                      {c.value && <div className="muted text-sm">{c.value}</div>}
                    </div>
                    <div className="row">
                      <button className="btn btn-sm" onClick={() => resolve(c.id, "accept")}>
                        Save
                      </button>
                      <button className="btn btn-sm btn-ghost" onClick={() => resolve(c.id, "reject")}>
                        Skip
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card card-pad-lg mt-8 center">
          <h2>Did I understand you correctly?</h2>
          <p className="muted text-sm mt-2">
            You can always refine any of this later in your Life Profile.
          </p>
          <div className="row mt-6" style={{ justifyContent: "center" }}>
            <button className="btn" onClick={onBack} disabled={completing}>
              Something needs changing
            </button>
            <button
              className="btn btn-primary"
              onClick={async () => {
                setCompleting(true);
                onComplete();
              }}
              disabled={completing}
            >
              {completing ? "Building your plan…" : "Yes — build my plan"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
